import { NVButton } from '@nv/basic-components'
import ChevronIcon from '@nv/images/design/chevron-icon'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import styles from './Pagination.module.scss'

export const Pagination = ({
  currentPageNum,
  dataLength,
  onPageChange,
  numRowsPerPage,
  paginationLabel,
  setNewNumRowsPerPage,
  onNumRowsChange,
}) => {
  const { t } = useTranslation()
  const totalPages = Math.max(1, Math.ceil(dataLength / numRowsPerPage))
  const isFirstPage = currentPageNum === 0
  const isLastPage = currentPageNum === totalPages - 1
  const step = 5

  const createPageNumbers = () => {
    const pages = []
    const maxVisible = 3
    const startPage = Math.max(0, Math.min(currentPageNum - 1, totalPages - maxVisible))
    for (let i = startPage; i < Math.min(startPage + maxVisible, totalPages); i++) pages.push(i)
    return pages
  }

  const onDotsClick = (direction) => {
    let newPage
    if (direction === 'left') newPage = Math.max(currentPageNum - step, 0)
    else newPage = Math.min(currentPageNum + step, totalPages - 1)
    onPageChange(newPage)
  }

  return (
    <div className={styles['container']}>
      <div className={styles['page-info']}>
        <span className={styles['text']}>
          {t('admin.electorsList.showing')}
          <span className={styles['value']}>{Math.min(currentPageNum * numRowsPerPage + 1, dataLength)}</span>
          {t('admin.electorsList.to')}
          <span className={styles['value']}>{Math.min((currentPageNum + 1) * numRowsPerPage, dataLength)}</span>
          {t('admin.electorsList.of')}
          <span className={styles['value']}>{dataLength}</span>
          {t(`${paginationLabel}`)}
        </span>

        {/* TODO: add back with corresponding code */}
        {/* {dataLength > 100 && (
          <SelectRows newNumRowsPerPage={numRowsPerPage} setNewNumRowsPerPage={setNewNumRowsPerPage} onNumRowsChange={onNumRowsChange} />
        )} */}
      </div>
      <div className={styles['pagination-container']}>
        {/* Previous Chevron */}
        <NVButton
          variant='iconOnly'
          onClick={() => !isFirstPage && onPageChange(currentPageNum - 1)}
          disabled={isFirstPage}
          icon={<ChevronIcon />}
          classes={{ container: clsx(styles['prev__chevron'], { [styles['disabled']]: isFirstPage }) }}
          ariaLabel={t('pages.jumpBackward', { step: step })}
        />

        {/* Left Dots */}
        {currentPageNum > 2 && totalPages > 4 && (
          <>
            <NVButton variant='text' text='1' onClick={() => onPageChange(0)} classes={{ container: styles['pagination-item'] }} />
            <NVButton
              variant='text'
              text='…'
              onClick={() => onDotsClick('left')}
              classes={{ container: clsx(styles['pagination-item'], styles['dots']) }}
              ariaLabel={t('pages.jumpForward', { step: step })}
            />
          </>
        )}

        {/* Page Numbers */}
        {createPageNumbers().map((page) => {
          const isCurrent = page === currentPageNum
          return (
            <NVButton
              key={page}
              variant='text'
              text={(page + 1).toString()}
              onClick={() => !isCurrent && onPageChange(page)}
              disabled={isCurrent}
              ariaLabel={`Page ${page + 1}`}
              ariaCurrent={isCurrent ? 'page' : undefined}
              classes={{
                container: clsx(styles['pagination-item'], {
                  [styles['selected']]: isCurrent,
                }),
              }}
            />
          )
        })}

        {/* Right Dots */}
        {currentPageNum < totalPages - 3 && totalPages > 4 && (
          <>
            <NVButton
              variant='text'
              text='…'
              onClick={() => onDotsClick('right')}
              ariaLabel={`Jump forward ${step} pages`}
              classes={{ container: clsx(styles['pagination-item'], styles['dots']) }}
            />
            <NVButton
              variant='text'
              text={totalPages.toString()}
              onClick={() => onPageChange(totalPages - 1)}
              classes={{ container: styles['pagination-item'] }}
            />
          </>
        )}

        {/* Next Chevron */}
        <NVButton
          variant='iconOnly'
          onClick={() => !isLastPage && onPageChange(currentPageNum + 1)}
          disabled={isLastPage}
          icon={<ChevronIcon />}
          classes={{ container: clsx(styles['next__chevron'], { [styles['disabled']]: isLastPage }) }}
        />
      </div>
    </div>
  )
}

export default Pagination
